import {
  BooleanField,
  DateField,
  DateTimeField,
  ModelGroup,
  NumberField,
  StringField,
  UnmanagedField
} from '@mng/model';
import {
  createFzKontoBezeichnungField,
  createFzKontoDienststelleRequiredField,
  createFzKontoExklusivAbberNrField,
  createFzKontoGiltBisField,
  createFzKontoGiltVonField,
  createFzKontoHinweisField,
  createFzKontoKontoField,
  createFzKontoKontoNameField,
  createFzKontoPersonalExplanationField,
  createFzKontoPersonalNummerRegexField,
  createFzKontoVerrechnungsartField,
  createFzPersNrErforderlichField
} from '../allgemein/fz-konto-base';
import { createVermittlerInfoBlattAbberGroup, createVermittlerInformationGroup } from '../allgemein/vermittler-model';
import { createUvPersonenDataModelGroup } from './uvperson.model';
import { StammdatenBase } from '../allgemein/stammdaten-base';

export const UV_LEISTUNGSART_UNFALLTOD_ID = '1';

export function createUvAngebotModel(): ModelGroup {
  const vermittlerGroup: ModelGroup = createVermittlerInformationGroup().withPathPrefix('UV');
  const vermittlerInfoBlattAbberGroup: ModelGroup = createVermittlerInfoBlattAbberGroup()
    .withPathPrefix('UV')
    // Workaround zum Verhindern eines zweiten VermittlerDto Imports
    .withImportDef({ localImport: true });

  const stammdaten = new StammdatenBase();
  stammdaten.init(true, true);
  stammdaten.adresse.withInterfaceName('UvVnStammdatenAdresse');
  const stammdatenBisherigerVnModelGroup = stammdaten.createStammdatenModelGroup().withInterfaceName('UvVnStammdaten');
  stammdatenBisherigerVnModelGroup.id = 'vertragBisherigerVn';

  const model: ModelGroup = new ModelGroup('uvAngebotData').addItems(
    new StringField('id').withReadOnly(),
    new DateTimeField('modifiedDate').withReadOnly(),
    new BooleanField('kollektiv'),
    new BooleanField('vertragsAenderung').withReadOnly(),
    new DateField('vertragszustandPerDatum', 'Datum an dem der Vertragszustand ausgelesen wurde').withReadOnly(),
    new BooleanField('vertragsAenderungInitialisiert').withReadOnly(),
    new BooleanField('iddBeratungDurchgefuehrt').withReadOnly(),
    new NumberField('vertragsNummer').withReadOnly(),
    new NumberField('versionsNummer').withReadOnly(),
    new NumberField('vertragJahresNettoPraemie').withReadOnly(),
    new DateField('vertragsAblaufDatum', 'Datum an dem der Vertrag abläuft').withReadOnly(),
    new DateField('vertragsAenderungsDatum', 'Datum an dem der Vertrag geändert werden soll'),
    stammdatenBisherigerVnModelGroup,
    new UnmanagedField('tarif', 'Tarif'),
    createUvTarifZusatzDaten(),
    createUvAngebotFzKontoModelGroup(),
    new StringField('bestandFzKontoNr').withReadOnly(),
    vermittlerGroup,
    vermittlerInfoBlattAbberGroup,
    createVertragsdaten(),
    createBeratungsdaten()
  );
  model.addGroupObject(createUvPersonenDataModelGroup());

  return model;
}

function createVertragsdaten(): ModelGroup {
  return new ModelGroup('uvVertragsdaten')
    .addArrayGroupObject(createLeistungsarten())
    .withRemoveDataWhenNotRelevant(true);
}

function createLeistungsarten(): ModelGroup {
  return new ModelGroup('leistungsarten').withInterfaceName('UvLeistungsart').addItems(
    // Leistungsart Schlüssel
    new NumberField('id').withReadOnly(),
    // Variante
    new NumberField('var').withReadOnly(),
    // Karenz
    new NumberField('kar').withReadOnly(),
    // Partnernummer
    new NumberField('pnr').withReadOnly(),
    // Versicherungssumme
    new NumberField('vs').withReadOnly()
  );
}

function createBeratungsdaten(): ModelGroup {
  return new ModelGroup('beratungsdaten')
    .withInterfaceName('UvBeratungsdaten')
    .addItems(new StringField('tarifNr').withReadOnly())
    .addArrayGroupObject(createBeratungsdatenLeistungsarten())
    .withRemoveDataWhenNotRelevant(true);
}

function createBeratungsdatenLeistungsarten(): ModelGroup {
  return new ModelGroup('leistungsarten').withInterfaceName('UvBeratungsdatenLeistungsart').addItems(
    // Leistungsart Schlüssel
    new NumberField('id').withReadOnly(),
    // Variante
    new NumberField('var').withReadOnly(),
    // Karenz
    new NumberField('kar').withReadOnly(),
    // Partnernummer
    new NumberField('pnr').withReadOnly()
  );
}

export function createUvTarifZusatzDaten(): ModelGroup {
  return new ModelGroup('tarifZusatzDaten').addItems(new StringField('stornoPolizzeText').withReadOnly());
}

function createUvAngebotFzKontoModelGroup(): ModelGroup {
  const fzKonto: ModelGroup = new ModelGroup('fzKonto', 'FZ Konto')
    .withInterfaceName('UvAngebotFzKonto')
    .addItems(...createUvFzKontoFields());
  return fzKonto;
}

export function createUvFzKontoFields(): StringField[] {
  return [
    createFzKontoKontoField(),
    createFzKontoKontoNameField(),
    createFzKontoExklusivAbberNrField().withRemoveDataWhenNotRelevant().asArray(),
    createFzKontoVerrechnungsartField(),
    createFzKontoBezeichnungField(),
    createFzKontoHinweisField(),
    createFzKontoGiltVonField(),
    createFzKontoGiltBisField(),
    createFzKontoPersonalNummerRegexField(),
    createFzKontoPersonalExplanationField(),
    createFzKontoDienststelleRequiredField(),
    createFzPersNrErforderlichField()
  ];
}
